import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ResponsiveContainer, ResponsiveContent, ResponsiveFooter } from '../ui/ResponsiveLayout';
import { useSessionPersistence } from '../../hooks/useSessionPersistence';
import { TaxReturnIcon, TaxDocumentIcon, DocumentsIcon } from '../ui/Icons';
import Modal from '../ui/Modal';
import FileUpload from '../ui/FileUpload';
import { documentService } from '../../services/documentService';
import type { AppDocument } from '../../types/document';

interface TaxDocument {
  id: string;
  type: 'federal' | 'state' | 'property' | 'other';
  year: string;
  description: string;
  status: 'filed' | 'pending' | 'extension';
  amount?: number;
  documents: AppDocument[];
  notes?: string;
}

interface TaxCategory {
  key: 'federal' | 'state' | 'property' | 'other';
  label: string;
  Icon: React.FC<{ className?: string }>;
  description: string;
}

const normalizeDocuments = (docs: unknown): AppDocument[] => {
  return documentService.normalizeMany(docs);
};

const Frame239: React.FC = () => {
  const navigate = useNavigate();
  const { session, updateTaskProgress } = useSessionPersistence();

  const [selectedCategory, setSelectedCategory] = useState<TaxCategory | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingDocId, setEditingDocId] = useState<string | null>(null);

  const [taxDocs, setTaxDocs] = useState<TaxDocument[]>(() => {
    const sessionDocs = session?.taskProgress?.tax_documents?.answers?.documents;
    if (Array.isArray(sessionDocs)) {
      console.log('[Frame239] Loading from session:', sessionDocs);
      return sessionDocs.map((doc: TaxDocument) => ({
        ...doc,
        documents: normalizeDocuments(doc.documents || []),
      }));
    }

    try {
      const backupData = localStorage.getItem('tax-documents-backup');
      if (backupData) {
        const data = JSON.parse(backupData);
        if (Array.isArray(data)) {
          console.log('[Frame239] Loading from localStorage backup:', data);
          return data.map((doc: TaxDocument) => ({
            ...doc,
            documents: normalizeDocuments(doc.documents || []),
          }));
        }
      }
    } catch (error) {
      console.error('[Frame239] Failed to load from backup:', error);
    }

    return [];
  });

  const [year, setYear] = useState(new Date().getFullYear().toString());
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState<TaxDocument['status']>('filed');
  const [amount, setAmount] = useState('');
  const [notes, setNotes] = useState('');
  const [documents, setDocuments] = useState<AppDocument[]>([]);
  const [isUploadingDocuments, setIsUploadingDocuments] = useState(false);

  const categories: TaxCategory[] = [
    {
      key: 'federal',
      label: 'Federal Tax\nReturns',
      Icon: TaxReturnIcon,
      description: 'IRS Form 1040 and related schedules'
    },
    {
      key: 'state',
      label: 'State Tax\nReturns',
      Icon: TaxReturnIcon,
      description: 'State income tax returns and related forms'
    },
    {
      key: 'property',
      label: 'Property Tax\nRecords',
      Icon: TaxDocumentIcon,
      description: 'Property tax assessments and payment records'
    },
    {
      key: 'other',
      label: 'Other Tax\nDocuments',
      Icon: TaxDocumentIcon,
      description: 'Gift tax returns, estate tax returns, and other tax-related documents'
    }
  ];

  useEffect(() => {
    const sessionDocs = session?.taskProgress?.tax_documents?.answers?.documents;

    if (!Array.isArray(sessionDocs)) return;

    const normalizedSessionDocs = sessionDocs.map((doc: TaxDocument) => ({
      ...doc,
      documents: normalizeDocuments(doc.documents || []),
    }));

    const sessionStr = JSON.stringify(normalizedSessionDocs);
    const localStr = JSON.stringify(taxDocs);

    if (sessionStr !== localStr) {
      console.log('[Frame239] Syncing tax docs from session:', normalizedSessionDocs);
      setTaxDocs(normalizedSessionDocs);

      try {
        localStorage.setItem('tax-documents-backup', JSON.stringify(normalizedSessionDocs));
        console.log('[Frame239] Synced session docs to localStorage backup');
      } catch (error) {
        console.error('[Frame239] Failed to sync to backup:', error);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.taskProgress?.tax_documents?.answers?.documents]);

  const openDocument = (doc: AppDocument) => {
    if (!doc.id) return;
    documentService.open(doc.id);
  };

  const downloadDocument = async (doc: AppDocument) => {
    if (!doc.id) return;

    try {
      await documentService.download(doc.id, doc.name);
    } catch (error) {
      console.error('[Frame239] Failed to download document:', error);
      alert('Failed to download document.');
    }
  };

  const resetForm = () => {
    setYear(new Date().getFullYear().toString());
    setDescription('');
    setStatus('filed');
    setAmount('');
    setNotes('');
    setDocuments([]);
    setEditingDocId(null);
    setIsUploadingDocuments(false);
  };

  const handleCategoryClick = (category: TaxCategory) => {
    setSelectedCategory(category);
    resetForm();
    setIsModalOpen(true);
  };

  const handleFileUpload = async (files: File[]) => {
    if (!files.length) return;

    setIsUploadingDocuments(true);

    try {
      const uploadedDocs = await Promise.all(
        files.map((file) => documentService.upload(file))
      );

      setDocuments((prev) => [...prev, ...uploadedDocs]);
    } catch (error) {
      console.error('[Frame239] Failed to upload document(s):', error);
      alert('Failed to upload one or more documents.');
    } finally {
      setIsUploadingDocuments(false);
    }
  };

  const handleRemoveDocument = async (index: number) => {
    const docToRemove = documents[index];
    if (!docToRemove) return;

    try {
      await documentService.delete(docToRemove.id);
    } catch (error) {
      console.error('[Frame239] Failed to delete unsaved document:', error);
      alert('Failed to delete document.');
      return;
    }

    setDocuments((prev) => prev.filter((_, i) => i !== index));
  };

  const handleEditDoc = (doc: TaxDocument) => {
    setEditingDocId(doc.id);
    setSelectedCategory(categories.find(c => c.key === doc.type) || null);
    setYear(doc.year);
    setDescription(doc.description);
    setStatus(doc.status);
    setAmount(doc.amount ? doc.amount.toString() : '');
    setNotes(doc.notes || '');
    setDocuments(normalizeDocuments(doc.documents || []));
    setIsModalOpen(true);
  };

  const handleDeleteDoc = async (docId: string) => {
    if (!confirm('Are you sure you want to delete this tax document?')) return;

    const docToDelete = taxDocs.find((d) => d.id === docId);
    const attachedDocs = docToDelete?.documents || [];

    for (const doc of attachedDocs) {
      try {
        await documentService.delete(doc.id);
      } catch (error) {
        console.error('[Frame239] Failed to delete document while deleting tax document:', error);
      }
    }

    const updatedDocs = taxDocs.filter(d => d.id !== docId);
    setTaxDocs(updatedDocs);

    try {
      localStorage.setItem('tax-documents-backup', JSON.stringify(updatedDocs));
      console.log('[Frame239] Deleted tax document from backup');
    } catch (error) {
      console.error('[Frame239] Failed to update backup:', error);
    }

    updateTaskProgress('tax_documents', { documents: updatedDocs }, 'in-progress').catch(error => {
      console.error('[Frame239] Session delete failed:', error);
    });
  };

  const handleRemoveAttachedDocument = async (taxDocId: string, documentIndex: number) => {
    const taxDoc = taxDocs.find((d) => d.id === taxDocId);
    const docToRemove = taxDoc?.documents?.[documentIndex];
    if (!docToRemove) return;

    try {
      await documentService.delete(docToRemove.id);
    } catch (error) {
      console.error('[Frame239] Failed to delete saved document:', error);
      alert('Failed to delete document.');
      return;
    }

    const updatedDocs = taxDocs.map((doc) => {
      if (doc.id !== taxDocId) return doc;
      return {
        ...doc,
        documents: normalizeDocuments(doc.documents || []).filter((_, index) => index !== documentIndex),
      };
    });

    setTaxDocs(updatedDocs);

    try {
      localStorage.setItem('tax-documents-backup', JSON.stringify(updatedDocs));
    } catch (error) {
      console.error('[Frame239] Failed to update backup:', error);
    }

    updateTaskProgress('tax_documents', { documents: updatedDocs }, 'in-progress').catch(error => {
      console.error('[Frame239] Session update failed:', error);
    });
  };

  const handleSave = () => {
    if (!selectedCategory || !year || !description || documents.length === 0) {
      alert('Please fill in all required fields and upload at least one document');
      return;
    }

    const docData: TaxDocument = {
      id: editingDocId || Date.now().toString(),
      type: selectedCategory.key,
      year,
      description,
      status,
      amount: amount ? parseFloat(amount.replace(/,/g, '')) : undefined,
      documents,
      notes
    };

    const updatedDocs = editingDocId
      ? taxDocs.map(d => (d.id === editingDocId ? docData : d))
      : [...taxDocs, docData];

    setTaxDocs(updatedDocs);

    try {
      localStorage.setItem('tax-documents-backup', JSON.stringify(updatedDocs));
      console.log('[Frame239] Saved tax documents to backup');
    } catch (error) {
      console.error('[Frame239] Failed to save backup:', error);
    }

    updateTaskProgress('tax_documents', { documents: updatedDocs }, 'in-progress').catch(error => {
      console.error('[Frame239] Session save failed:', error);
    });

    setIsModalOpen(false);
    resetForm();
  };

  const handleNext = async () => {
    await updateTaskProgress('tax_documents', { documents: taxDocs }, 'in-progress');
    navigate('/frame-213');
  };

  const handleBack = () => navigate('/frame-190');

  return (
    <ResponsiveContainer background="stone">
      <ResponsiveContent>
        <div className="max-w-4xl">
          <h1 className="text-cyan-900 text-3xl font-semibold font-['Test_Domaine_Display'] mb-8">
            Tax Documents and Records
          </h1>

          <p className="text-stone-800 text-lg font-medium font-['Work_Sans'] mb-12">
            Upload and organize your tax documents. This will help your loved ones understand your tax situation and locate important records.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {categories.map(category => (
              <button
                key={category.key}
                onClick={() => handleCategoryClick(category)}
                className="p-6 bg-white rounded-lg border border-stone-200 hover:border-cyan-500 transition-colors text-left relative"
              >
                <div className="flex items-center gap-4">
                  <category.Icon className="w-8 h-8 text-cyan-600 flex-shrink-0" />
                  <div>
                    <h3 className="font-medium text-stone-800 whitespace-pre-line leading-tight">{category.label}</h3>
                    <p className="text-sm text-stone-600 mt-1">{category.description}</p>
                  </div>
                </div>
              </button>
            ))}
          </div>

          {taxDocs.length > 0 && (
            <div className="mt-12">
              <h2 className="text-stone-800 text-xl font-semibold font-['Work_Sans'] mb-4">
                Added Tax Documents
              </h2>
              <div className="space-y-3">
                {taxDocs.map(doc => {
                  const category = categories.find(c => c.key === doc.type);
                  return (
                    <div key={doc.id} className="bg-white rounded-lg border border-stone-200 shadow-sm p-4 sm:p-6 overflow-hidden max-w-full">
                      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                        
                        <div className="flex flex-col gap-2 min-w-0 flex-1 sm:flex-row sm:items-start sm:gap-4">
                          {category && <category.Icon className="w-6 h-6 text-cyan-700 flex-shrink-0" />}

                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-stone-800 break-words">
                              {doc.description} ({doc.year})
                            </div>

                            <div className="text-sm text-stone-500 mt-1 break-words">
                              Status: {doc.status.charAt(0).toUpperCase() + doc.status.slice(1)}
                              {doc.amount !== undefined && ` • Amount: $${doc.amount.toLocaleString()}`}
                            </div>

                            {doc.notes && (
                              <div className="text-sm text-stone-600 mt-2 break-words">
                                {doc.notes}
                              </div>
                            )}

                            <div className="text-sm text-cyan-600 mt-2">
                              {doc.documents.length} document(s) attached
                            </div>
                          </div>
                        </div>

                        <div className="flex flex-wrap gap-2 sm:flex-nowrap sm:shrink-0">
                          <button
                            onClick={() => handleEditDoc(doc)}
                            className="text-cyan-600 hover:text-cyan-700 px-3 py-1 rounded border border-cyan-200 hover:border-cyan-300"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeleteDoc(doc.id)}
                            className="text-red-600 hover:text-red-700 px-3 py-1 rounded border border-red-200 hover:border-red-300"
                          >
                            Delete
                          </button>
                        </div>
                      </div>

                      {doc.documents.length > 0 && (
                        <div className="mt-4 space-y-2">
                          {normalizeDocuments(doc.documents).map((attachedDoc, index) => (
                            <div
                              key={`${doc.id}-${attachedDoc.id}-${index}`}
                              className="flex flex-col gap-3 rounded-md border border-stone-200 bg-stone-50 px-2 sm:px-3 py-3 sm:flex-row sm:items-center sm:justify-between overflow-hidden max-w-full"
                            >
                              <button
                                type="button"
                                onClick={() => openDocument(attachedDoc)}
                                className="flex items-start gap-2 min-w-0 w-full text-left sm:flex-1"
                              >
                                <DocumentsIcon className="w-4 h-4 text-cyan-700 flex-shrink-0 mt-0.5" />
                                <span className="text-sm font-medium text-stone-800 break-all whitespace-normal min-w-0">
                                  {attachedDoc.name}
                                </span>
                              </button>

                              <div className="flex flex-wrap items-center gap-x-3 gap-y-2 sm:shrink-0">
                                <button
                                  type="button"
                                  onClick={() => downloadDocument(attachedDoc)}
                                  className="text-cyan-600 hover:text-cyan-800 text-xs font-medium"
                                >
                                  Download
                                </button>

                                <button
                                  type="button"
                                  onClick={() => handleRemoveAttachedDocument(doc.id, index)}
                                  className="text-red-600 hover:text-red-700 text-xs font-medium"
                                >
                                  Remove
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </ResponsiveContent>

      <Modal
        open={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          resetForm();
        }}
        onOk={handleSave}
        onCancel={() => {
          setIsModalOpen(false);
          resetForm();
        }}
        title={editingDocId ? 'Edit Tax Document' : (selectedCategory ? `Add ${selectedCategory.label.replace('\n', ' ')}` : '')}
        okText={editingDocId ? 'Save Changes' : 'Add Document'}
        cancelText="Cancel"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">Tax Year*</label>
            <input
              type="text"
              value={year}
              onChange={(e) => setYear(e.target.value)}
              className="w-full px-3 py-2 border border-neutral-300 rounded-md"
              placeholder="YYYY"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">Description*</label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full px-3 py-2 border border-neutral-300 rounded-md"
              placeholder="e.g., Federal Income Tax Return 2023"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">Status*</label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value as TaxDocument['status'])}
              className="w-full px-3 py-2 border border-neutral-300 rounded-md"
            >
              <option value="filed">Filed</option>
              <option value="pending">Pending</option>
              <option value="extension">Extension Filed</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">Amount (optional)</label>
            <div className="relative">
              <span className="absolute left-3 top-2">$</span>
              <input
                type="text"
                value={amount}
                onChange={(e) => {
                  const val = e.target.value.replace(/[^\d.,]/g, '');
                  setAmount(val);
                }}
                className="w-full pl-7 pr-3 py-2 border border-neutral-300 rounded-md"
                placeholder="0.00"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">Notes (optional)</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="w-full px-3 py-2 border border-neutral-300 rounded-md"
              rows={3}
              placeholder="Add any additional notes"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">Upload Documents*</label>
            <FileUpload onUpload={handleFileUpload} accept=".pdf,.jpg,.jpeg,.png" multiple />

            {isUploadingDocuments && (
              <div className="mt-3 text-sm text-stone-500">
                Uploading documents...
              </div>
            )}

            {documents.length > 0 && (
              <ul className="mt-2 space-y-2">
                {documents.map((doc, idx) => (
                  <li
                    key={`${doc.id}-${idx}`}
                    className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-sm text-stone-600 bg-stone-50 p-2 border border-stone-200 rounded"
                  >
                    <button
                      type="button"
                      onClick={() => openDocument(doc)}
                      className="flex items-start gap-2 min-w-0 w-full sm:flex-1 text-left"
                    >
                      <DocumentsIcon className="w-4 h-4 text-cyan-700 flex-shrink-0 mt-0.5" />
                      <span className="text-sm font-medium text-stone-800 truncate" title={doc.name}>
                        {doc.name}
                      </span>
                    </button>

                    <div className="flex flex-wrap items-center gap-2">
                      <button
                        type="button"
                        onClick={() => downloadDocument(doc)}
                        className="text-cyan-600 hover:text-cyan-800 text-xs font-medium"
                      >
                        Download
                      </button>

                      <button
                        onClick={() => handleRemoveDocument(idx)}
                        className="text-red-600 hover:text-red-700 font-medium text-xs shrink-0"
                      >
                        Remove
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </Modal>

      <ResponsiveFooter
        title="Inventory Your Assets"
        subtitle="Add your tax documents"
        progressPercent={87.51}
        onNext={handleNext}
        onBack={handleBack}
        nextText="Next"
        backText="Back"
      />
    </ResponsiveContainer>
  );
};

export default Frame239;